import React, { useMemo, useState } from "react";
import styled from "@emotion/styled";
import defaultMailIcon from "../../../Assets/Images/client-email-icon.svg";
import sendIcon from "../../../Assets/Images/email-send-icon.svg";
import eyeIcon from "../../../Assets/Images/Eye-Icon.svg";
import VybogTALLoaderAnimation from "../../Dynamic-Animations/DynamicAnimations";
import AnimationPlayer from "../../Animation-Player/AnimationPlayer";
import NoHistoryAnimation from "../../../Assets/Json/No-History-Animation.json";
import SidePopup from "../../SidePopup/SidePopup";
import EmailIcon from "../../../Assets/Images/email-history-icon.svg";

const ContentMainContainer = styled("div")({
  width: "100%",
});

const EmailHistoryContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  maxHeight: "520px",
  overflowY: "auto",
  padding: "0 8px 10px 0",
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
  },
  "&::-webkit-scrollbar-track": {
    borderRadius: "5px",
    backgroundColor: "#e6f1f5",
  },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: "5px",
    backgroundColor: "#1b52a729",
  },
});

const EmailHistoryGroup = styled("div")({
  position: "relative",
  paddingLeft: "34px",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "9px",
    top: "24px",
    bottom: "-18px",
    width: "2px",
    backgroundImage:
      "linear-gradient(to bottom, #e1e4ec 0 4px, transparent 4px 10px)",
    backgroundSize: "2px 10px",
    backgroundRepeat: "repeat-y",
  },
  "&:last-child::before": {
    bottom: "24px",
  },
});

const EmailHistoryGroupHeader = styled("div")({
  display: "grid",
  gridTemplateColumns: "auto 1fr",
  alignItems: "center",
  gap: "12px",
  marginBottom: "16px",
});

const EmailHistoryDateLabel = styled("span")({
  color: "#667085",
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
});

const EmailHistoryHeaderLine = styled("span")({
  height: "1px",
  backgroundColor: "#e6e8f0",
  width: "100%",
});

const EmailHistoryItems = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "14px",
});

const EmailHistoryItem = styled("div")({
  position: "relative",
  display: "grid",
  gridTemplateColumns: "48px minmax(0, 1fr) auto",
  alignItems: "center",
  gap: "14px",
  border: "1px solid #dfe3eb",
  borderRadius: "14px",
  boxShadow: "0px 3px 10px 0px #0000000D",
  backgroundColor: "var(--card__background)",
  padding: "16px 20px",
  minHeight: "74px",
  "&::before": {
    content: '""',
    position: "absolute",
    left: "-34px",
    top: "50%",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: "#9b8cff",
    border: "5px solid #f1eeff",
    boxShadow: "0 0 0 1px #ebe7ff",
    transform: "translateY(-50%)",
  },
  "@media screen and (max-width:768px)": {
    gridTemplateColumns: "40px minmax(0, 1fr)",
    alignItems: "flex-start",
    padding: "14px",
  },
});

const EmailHistoryIconBox = styled("div")(({ variant }) => ({
  width: "40px",
  height: "40px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor:
    variant === "blast"
      ? "#dff2ff"
      : variant === "client"
        ? "#d9f8e8"
        : "#eeeaff",
  border:
    variant === "blast"
      ? "1px solid #bde4fb"
      : variant === "client"
        ? "1px solid #b7ebd0"
        : "1px solid #ddd6fe",
}));

const EmailHistoryIcon = styled("img")({
  width: "20px",
  height: "20px",
  objectFit: "contain",
});

const EmailHistoryContent = styled("div")({
  minWidth: 0,
});

const EmailHistorySubject = styled("h4")({
  color: "var(--header__color)",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: 1.35,
  margin: "0 0 6px",
});

const EmailHistoryRecipient = styled("p")({
  color: "#666",
  fontSize: "14px",
  lineHeight: 1.35,
  margin: "0 0 5px",
});

const EmailHistoryMeta = styled("div")({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: "7px",
  color: "#667085",
  fontSize: "13px",
  lineHeight: 1.4,
});

const EmailHistoryMetaDate = styled("span")({
  color: "#5f6f89",
});

const EmailHistoryMoreBadge = styled("span")({
  borderRadius: "999px",
  backgroundColor: "#f2f4f7",
  color: "#667085",
  fontSize: "11px",
  fontWeight: 600,
  padding: "2px 8px",
});

const EmailHistoryActions = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-end",
  gap: "14px",
  minWidth: "112px",
  "@media screen and (max-width:768px)": {
    gridColumn: "2 / 3",
    alignItems: "flex-start",
    minWidth: "auto",
    gap: "8px",
  },
});

const EmailHistoryTypeBadge = styled("span")(({ variant }) => ({
  borderRadius: "999px",
  backgroundColor: variant === "blast" ? "#dff2ff" : "#eeeaff",
  color: variant === "blast" ? "#0277bd" : "#6860ee",
  fontSize: "12px",
  fontWeight: 500,
  padding: "4px 12px",
  whiteSpace: "nowrap",
}));

const EmailHistoryViewButton = styled("button")({
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  border: "none",
  background: "transparent",
  color: "#6860ee",
  fontSize: "12px",
  fontWeight: 500,
  padding: 0,
  cursor: "pointer",
});

const NoTrackingHistory = styled("div")({
  width: "100%",
});

const formatEmailType = (emailType = "") =>
  emailType
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ") || "Email";

const getValidDate = (dateValue) => {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatLogDate = (dateValue) => {
  const date = getValidDate(dateValue);
  if (!date) return "N/A";

  return date.toLocaleDateString("en-US", {
    day: "2-digit",
    month: "short",
  });
};

const formatLogTime = (dateValue) => {
  const date = getValidDate(dateValue);
  if (!date) return "N/A";

  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const getGroupLabel = (dateValue) => {
  const date = getValidDate(dateValue);
  if (!date) return "Earlier";

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfLogDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );
  const diffInDays = Math.floor(
    (startOfToday - startOfLogDate) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays <= 7) return "Last Week";
  return "Earlier";
};

const getInitials = (name = "") => {
  const words = name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2);

  return words.map((word) => word[0]?.toUpperCase()).join("") || "U";
};

const mapEmailLogToItem = (log, defaultVariant) => {
  const recipientEmails = Array.isArray(log?.recipient_emails)
    ? log.recipient_emails
    : [];
  const variant = log?.email_type === "job_blast" ? "blast" : defaultVariant;

  return {
    id: log?.id,
    subject: log?.subject || "No subject",
    recipients: recipientEmails[0] || "N/A",
    extraRecipients:
      recipientEmails.length > 1 ? `+${recipientEmails.length - 1} more` : "",
    sender: log?.user_name || "Unknown",
    date: formatLogDate(log?.created_at),
    time: formatLogTime(log?.created_at),
    type: formatEmailType(log?.email_type),
    variant,
    groupLabel: getGroupLabel(log?.created_at),
    message: log?.message || "N/A",
    jobId: log?.job_id || "N/A",
    userId: log?.user_id || "N/A",
    recipientEmails,
  };
};

function EmailsHistory({
  emailHistory = [],
  totalLogs = 0,
  isLoading = false,
  entityLabel = "record",
  mailIcon = defaultMailIcon,
  defaultVariant = "client",
}) {
  const [selectedEmailLog, setSelectedEmailLog] = useState(null);

  const emailHistoryGroups = useMemo(() => {
    const groupOrder = ["Today", "Yesterday", "Last Week", "Earlier"];
    const groupedItems = emailHistory.reduce((groups, log) => {
      const item = mapEmailLogToItem(log, defaultVariant);
      return {
        ...groups,
        [item.groupLabel]: [...(groups[item.groupLabel] || []), item],
      };
    }, {});

    return groupOrder
      .filter((label) => groupedItems[label]?.length)
      .map((label) => ({
        label,
        items: groupedItems[label],
      }));
  }, [defaultVariant, emailHistory]);

  const hasEmailHistory = emailHistoryGroups.length > 0;

  return (
    <>
      <ContentMainContainer className="history-contentMain-Container">
        {hasEmailHistory && (
          <div style={{ marginBottom: "18px" }}>
            {/* <h3
              style={{
                color: "var(--header__color)",
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: 1.3,
                margin: "0 0 4px",
              }}
            >
              Email History
            </h3> */}
            <div
              style={{
                color: "#667085",
                fontSize: "13px",
                fontWeight: 500,
                lineHeight: 1.4,
                marginTop: "-10px"
              }}
            >
              {totalLogs} emails logged
            </div>
          </div>
        )}
        <EmailHistoryContainer>
          {isLoading ? (
            <div
              style={{
                width: "100%",
                minHeight: "300px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VybogTALLoaderAnimation />
            </div>
          ) : hasEmailHistory ? (
            emailHistoryGroups.map((group) => (
              <EmailHistoryGroup key={group.label}>
                <EmailHistoryGroupHeader>
                  <EmailHistoryDateLabel>{group.label}</EmailHistoryDateLabel>
                  <EmailHistoryHeaderLine />
                </EmailHistoryGroupHeader>

                <EmailHistoryItems>
                  {group.items.map((item) => (
                    <EmailHistoryItem
                      key={item.id || `${group.label}-${item.subject}`}
                    >
                      <EmailHistoryIconBox variant={item.variant}>
                        <EmailHistoryIcon
                          src={item.variant === "blast" ? sendIcon : mailIcon}
                          alt=""
                        />
                      </EmailHistoryIconBox>

                      <EmailHistoryContent>
                        <EmailHistorySubject>{item.subject}</EmailHistorySubject>
                        <EmailHistoryRecipient>
                          {item.recipients}
                          {item.extraRecipients && (
                            <>
                              {" "}
                              <EmailHistoryMoreBadge>
                                {item.extraRecipients}
                              </EmailHistoryMoreBadge>
                            </>
                          )}
                        </EmailHistoryRecipient>
                        <EmailHistoryMeta>
                          <span>Sent by {item.sender}</span>
                          <span>&bull;</span>
                          <EmailHistoryMetaDate>{item.date}</EmailHistoryMetaDate>
                          <span>&bull; {item.time}</span>
                        </EmailHistoryMeta>
                      </EmailHistoryContent>

                      <EmailHistoryActions>
                        <EmailHistoryTypeBadge variant={item.variant}>
                          {item.type}
                        </EmailHistoryTypeBadge>
                        <EmailHistoryViewButton
                          type="button"
                          onClick={() => setSelectedEmailLog(item)}
                        >
                          <EmailHistoryIcon src={eyeIcon} alt="" />
                          View
                        </EmailHistoryViewButton>
                      </EmailHistoryActions>
                    </EmailHistoryItem>
                  ))}
                </EmailHistoryItems>
              </EmailHistoryGroup>
            ))
          ) : (
            <NoTrackingHistory>
              <div
                style={{
                  width: "100%",
                  minHeight: "300px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                }}
              >
                <h3
                  style={{
                    color: "#6665ea",
                    fontSize: "16px",
                    fontWeight: 700,
                    lineHeight: 1.4,
                    margin: 0,
                    textAlign: "center",
                  }}
                >
                  No email history found for this {entityLabel}.
                </h3>
                <AnimationPlayer
                  animationSrc={NoHistoryAnimation}
                  playerHeight="200px"
                  playerWidth="300px"
                />
              </div>
            </NoTrackingHistory>
          )}
        </EmailHistoryContainer>
      </ContentMainContainer>

      <SidePopup
        isOpen={Boolean(selectedEmailLog)}
        onClose={() => setSelectedEmailLog(null)}
        title="Email Details"
        direction="right"
        sidePopupWidth="45%"
        showHeader={true}
        headerBackgroundColor="var(--body__background)"
        titleColor="var(--header__color)"
        subTitleColor="#62748a"
        contentPadding="16px"
        headerContent={
          selectedEmailLog ? (
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <EmailHistoryIconBox
                variant={selectedEmailLog.variant}
                style={{ width: "34px", height: "34px", borderRadius: "10px" }}
              >
                <EmailHistoryIcon
                  src={
                    selectedEmailLog.variant === "blast" ? sendIcon : mailIcon
                  }
                  alt=""
                  style={{ width: "16px", height: "16px" }}
                />
              </EmailHistoryIconBox>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    color: "var(--header__color)",
                    fontSize: "14px",
                    fontWeight: 600,
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                  }}
                >
                  {selectedEmailLog.subject}
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginTop: "4px",
                  }}
                >
                  <EmailHistoryTypeBadge variant={selectedEmailLog.variant}>
                    {selectedEmailLog.type}
                  </EmailHistoryTypeBadge>
                  <span
                    style={{
                      color: "#667085",
                      fontSize: "12px",
                      fontWeight: 500,
                    }}
                  >
                    {selectedEmailLog.date} - {selectedEmailLog.time}
                  </span>
                </div>
              </div>
            </div>
          ) : null
        }
      >
        {selectedEmailLog && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "18px",
            }}
          >
            <div
              style={{
                borderBottom: "1px solid var(--border__color)",
                paddingBottom: "16px",
              }}
            >
              <div
                style={{
                  color: "var(--sidepopup-label-color)",
                  fontSize: "11px",
                  fontWeight: 600,
                  marginBottom: "10px",
                  textTransform: "uppercase",
                }}
              >
                Recipients
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: "8px" }}
              >
                {(selectedEmailLog.recipientEmails.length
                  ? selectedEmailLog.recipientEmails
                  : ["N/A"]
                ).map((email) => (
                  <div
                    key={email}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      minHeight: "32px",
                      borderRadius: "8px",
                      backgroundColor: "var(--body__background)",
                      color: "var(--header__color)",
                      fontSize: "13px",
                      fontWeight: 500,
                      padding: "7px 10px",
                      wordBreak: "break-word",
                    }}
                  >
                    <EmailHistoryIcon
                      src={EmailIcon}
                      alt="emailicon"
                      style={{ width: "18px", height: "16px", flexShrink: 0 }}
                    />
                    <span>{email}</span>
                  </div>
                ))}
              </div>
            </div>

            <div
              style={{
                borderBottom: "1px solid var(--border__color)",
                paddingBottom: "16px",
              }}
            >
              <div
                style={{
                  color: "var(--sidepopup-label-color)",
                  fontSize: "11px",
                  fontWeight: 600,
                  marginBottom: "10px",
                  textTransform: "uppercase",
                }}
              >
                Sent By
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#eeeaff",
                    color: "#6860ee",
                    fontSize: "11px",
                    fontWeight: 700,
                    flexShrink: 0,
                    marginLeft:"2px",
                  }}
                >
                  {getInitials(selectedEmailLog.sender)}
                </span>
                <div style={{ display: "flex", minWidth: 0 }}>
                  <span
                    style={{
                      color: "var(--header__color)",
                      fontSize: "13px",
                      fontWeight: 500,
                      wordBreak: "break-word",
                    }}
                  >
                    {selectedEmailLog.sender}
                  </span>
                </div>
              </div>
            </div>

            <div
              style={{
                borderBottom: "1px solid var(--border__color)",
                paddingBottom: "16px",
              }}
            >
              <div
                style={{
                  color: "var(--sidepopup-label-color)",
                  fontSize: "11px",
                  fontWeight: 600,
                  marginBottom: "10px",
                  textTransform: "uppercase",
                }}
              >
                Subject
              </div>
              <div
                style={{
                  minHeight: "32px",
                  borderRadius: "8px",
                  backgroundColor: "var(--body__background)",
                  color: "var(--header__color)",
                  fontSize: "13px",
                  fontWeight: 500,
                  lineHeight: 1.5,
                  padding: "8px 10px",
                  wordBreak: "break-word",
                }}
              >
                {selectedEmailLog.subject}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
              }}
            >
              <div
                style={{
                  color: "var(--sidepopup-label-color)",
                  fontSize: "11px",
                  fontWeight: 600,
                  textTransform: "uppercase",
                }}
              >
                Message
              </div>
              <p
                style={{
                  color: "var(--header__color)",
                  fontSize: "13px",
                  lineHeight: 1.6,
                  margin: 0,
                  minHeight: "160px",
                  border: "1px solid var(--border__color)",
                  borderRadius: "10px",
                  padding: "16px",
                  backgroundColor: "var(--card__background)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {selectedEmailLog.message}
              </p>
            </div>
          </div>
        )}
      </SidePopup>
    </>
  );
}

export default EmailsHistory;
